import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Dumbbell, HeartPulse, Cross, Check, Star } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Reveal, MaskLine, EASE } from "@/components/Reveal";
import { toast } from "sonner";

const IMG = {
  hero: "https://images.unsplash.com/photo-1615260478820-ddc3a5051ff2",
  church: "https://images.unsplash.com/photo-1589483232748-515c025575bc",
  community: "https://images.unsplash.com/photo-1517456793572-1d8efd6dc135",
  forest: "https://images.unsplash.com/photo-1566231270035-0aaca1fd2bbf",
};

const CHAPTERS = [
  { n: "01", t: "Made in His Image", b: "Every man carries the fingerprint of God. We help men remember who they are — sons, called, not condemned." },
  { n: "02", t: "Whole-Man Wellness", b: "Body, heart, and spirit were never meant to be divided. We pursue physical strength, emotional honesty, and spiritual depth together." },
  { n: "03", t: "Brotherhood That Sharpens", b: "Iron sharpens iron. No man walks this road alone. We build together where honesty is safe and growth is expected." },
  { n: "04", t: "A Legacy That Lasts", b: "We raise men who lead their households, mentor the next generation, and leave a faithful legacy for the sons who follow." },
];

const PILLARS = [
  { icon: Dumbbell, t: "Physical", s: "Stewardship", b: "Honoring the body as a temple through movement, rest, and discipline." },
  { icon: HeartPulse, t: "Emotional", s: "Honesty", b: "Naming what we carry, breaking isolation, and healing in community." },
  { icon: Cross, t: "Spiritual", s: "Surrender", b: "Rooted in Christ, growing in prayer, Scripture, and daily obedience." },
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const [devotionals, setDevotionals] = useState([]);
  const [events, setEvents] = useState([]);
  const [ratings, setRatings] = useState({ average: 0, count: 0, ratings: [] });
  const [marqueeWords, setMarqueeWords] = useState(["Physical", "Emotional", "Spiritual", "Brotherhood", "Leadership", "Discipleship", "Restoration"]);
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: "", message: "", type: "General" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/devotionals").then((r) => setDevotionals(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/ratings").then((r) => setRatings(r.data)).catch(() => {});
    api.get("/public-config").then((r) => { if (r.data?.marquee_words?.length) setMarqueeWords(r.data.marquee_words); }).catch(() => {});
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent");
      setForm({ name: "", email: "", organization: "", subject: "", message: "", type: "General" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-bone">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 z-0">
          <img src={IMG.hero} alt="Man praying at sunrise" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-bone via-bone/40 to-charcoal/30" />
        </motion.div>

        <div className="aurora-layer absolute inset-0 z-[1] pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="aurora aurora-1" />
          <div className="aurora aurora-2" />
          <div className="aurora aurora-3" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-36 md:pt-44 pb-20 md:pb-28">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-mono text-xs uppercase tracking-[0.3em] font-bold text-white mb-8">
            <MaskLine delay={0.2}>A Christ-Centered Movement for Men</MaskLine>
          </motion.div>
          <h1 className="font-serif font-extralight text-[13vw] md:text-[9vw] lg:text-[8.5vw] leading-[0.9] tracking-tight text-charcoal max-w-[15ch]">
            <MaskLine delay={0.35}>Become the</MaskLine>
            <MaskLine delay={0.5} className="italic">man</MaskLine>
            <MaskLine delay={0.65}>you were made</MaskLine>
            <MaskLine delay={0.8}>to be.</MaskLine>
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <Link to="/register" data-testid="hero-join-btn"
              className="group inline-flex items-center gap-3 bg-charcoal text-bone px-8 py-4 font-mono text-xs uppercase tracking-widest hover:bg-olive transition-colors duration-300">
              Join the Movement <ArrowRight size={16} strokeWidth={1.5} className="hover-arrow" />
            </Link>
            <a href="#mission" className="font-sans text-charcoal/70 hover:text-charcoal transition-colors border-b border-charcoal/30 pb-1">
              Read our mission
            </a>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-bone border-y border-charcoal/10 py-4">
        <Marquee speed={30} gradient={false}>
          {marqueeWords.map((w, i) => (
            <span key={i} className="font-serif text-xl md:text-3xl italic text-black mx-10">{w} <span className="text-gold not-italic mx-4">✦</span></span>
          ))}
        </Marquee>
      </div>

      {/* MISSION / MANIFESTO */}
      <section id="mission" className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-charcoal/50">Our Mission</span>
          <h2 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-6 max-w-[20ch]">
            We exist to empower men — and equip the ministries that shape them.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mt-24">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08} className={i % 2 === 0 ? "md:col-span-7" : "md:col-span-5 md:mt-16"}>
              <div className="border-t border-charcoal/20 pt-8">
                <span className="font-mono text-sm text-gold">{c.n}</span>
                <h3 className="font-serif text-3xl md:text-4xl mt-4 tracking-tight">{c.t}</h3>
                <p className="font-sans text-charcoal/70 leading-relaxed mt-4 max-w-md">{c.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-sand py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal><span className="font-mono text-xs uppercase tracking-[0.3em] text-charcoal/50">Whole-Man Wellness</span></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10 mt-10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.1} className="bg-sand">
                <div className="p-10 md:p-12 h-full group hover:bg-bone transition-colors duration-500">
                  <p.icon size={32} strokeWidth={1.2} className="text-olive" />
                  <div className="mt-24">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{p.s}</span>
                    <h3 className="font-serif text-4xl mt-2">{p.t}</h3>
                    <p className="font-sans text-charcoal/70 leading-relaxed mt-4">{p.b}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BROTHERHOOD (dark) */}
      <section id="brotherhood" className="relative bg-charcoal text-bone grain overflow-hidden py-24 md:py-40">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">For Every Man</span>
            <h2 className="heading-light font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight mt-6">
              No man was meant to walk alone.
            </h2>
            <p className="font-sans text-bone/60 leading-relaxed mt-8 max-w-lg">
              We walk with men in every season — sharpening one another in honesty, prayer,
              and encouragement as we grow together into the men God made us to be.
            </p>
            <ul className="mt-10 space-y-4">
              {["Mentorship & Discipleship", "Small Groups & Brotherhood", "Prayer & Encouragement", "Retreats & Gatherings"].map((s) => (
                <li key={s} className="flex items-center gap-4 font-sans text-bone/80">
                  <Check size={18} strokeWidth={1.5} className="text-gold" /> {s}
                </li>
              ))}
            </ul>
            <Link to="/register" data-testid="brotherhood-cta"
              className="group inline-flex items-center gap-3 mt-12 border border-bone/30 px-8 py-4 font-mono text-xs uppercase tracking-widest hover:bg-bone hover:text-charcoal transition-colors duration-300">
              Join the Movement <ArrowUpRight size={16} strokeWidth={1.5} className="hover-arrow" />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6">
            <div className="relative overflow-hidden">
              <img src={IMG.church} alt="Church sanctuary" className="w-full h-[560px] object-cover arch" />
              <div className="absolute inset-0 bg-black/10 arch" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* DEVOTIONALS PREVIEW */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-charcoal/50">Daily Bread</span>
            <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight mt-4">Devotionals & Encouragement</h2>
          </Reveal>
          <Link to="/devotionals" className="group font-mono text-xs uppercase tracking-widest flex items-center gap-2 hover:text-olive transition-colors">
            All Devotionals <ArrowRight size={14} className="hover-arrow" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          {devotionals.map((d, i) => (
            <Reveal key={d.id} delay={i * 0.1}>
              <Link to="/devotionals" className="group block border border-charcoal/10 hover:border-charcoal transition-colors duration-500">
                <div className="overflow-hidden h-56">
                  <img src={d.image} alt={d.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{d.category} · {d.scripture}</span>
                  <h3 className="font-serif text-2xl mt-3 tracking-tight">{d.title}</h3>
                  <p className="font-sans text-sm text-charcoal/60 mt-3 line-clamp-3">{d.body}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EVENTS + COMMUNITY */}
      <section className="bg-sand py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="relative overflow-hidden h-full min-h-[400px]">
              <img src={IMG.community} alt="Brotherhood" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-charcoal/20" />
              <div className="absolute bottom-8 left-8 text-bone">
                <span className="font-serif text-4xl italic">Brotherhood</span>
              </div>
            </div>
          </Reveal>
          <div className="md:col-span-7">
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-charcoal/50">Gather Together</span>
              <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight mt-4 mb-10">Upcoming Events</h2>
            </Reveal>
            <div className="space-y-px bg-charcoal/10 border border-charcoal/10">
              {events.map((e, i) => (
                <Reveal key={e.id} delay={i * 0.08} className="bg-sand">
                  <Link to="/events" className="group flex items-center justify-between gap-6 p-8 hover:bg-bone transition-colors duration-400">
                    <div>
                      <span className="font-mono text-xs text-gold">{new Date(e.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                      <h3 className="font-serif text-2xl mt-1 tracking-tight">{e.title}</h3>
                      <p className="font-sans text-sm text-charcoal/60 mt-1">{e.location}</p>
                    </div>
                    <ArrowUpRight size={22} strokeWidth={1.3} className="hover-arrow shrink-0" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RATINGS / TESTIMONIALS */}
      {ratings.count > 0 && (
        <section className="bg-charcoal text-bone grain relative overflow-hidden py-24 md:py-36" data-testid="ratings-section">
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">From Our Supporters</span>
              <div className="flex items-end gap-6 mt-6 flex-wrap">
                <span className="font-serif text-7xl md:text-8xl leading-none heading-light">{ratings.average}</span>
                <div className="pb-3">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={22} strokeWidth={1.3} className={ratings.average >= n ? "fill-gold text-gold" : "text-bone/30"} />
                    ))}
                  </div>
                  <span className="font-mono text-xs uppercase tracking-widest text-bone/50 mt-2 block">{ratings.count} rating{ratings.count === 1 ? "" : "s"}</span>
                </div>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              {ratings.ratings.slice(0, 6).filter((r) => r.text).map((r, i) => (
                <Reveal key={r.id} delay={(i % 3) * 0.08}>
                  <div className="border border-bone/15 p-8 h-full">
                    <div className="flex gap-1 mb-4">
                      {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} strokeWidth={1.3} className={r.stars >= n ? "fill-gold text-gold" : "text-bone/25"} />)}
                    </div>
                    <p className="font-serif text-xl italic leading-snug heading-light">"{r.text}"</p>
                    <span className="font-mono text-xs uppercase tracking-widest text-bone/50 mt-6 block">— {r.name}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          <Reveal className="md:col-span-5">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-charcoal/50">Reach Out</span>
            <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight mt-6 leading-[1.05]">
              Let's start a conversation.
            </h2>
            <p className="font-sans text-charcoal/70 leading-relaxed mt-8 max-w-md">
              Whether you're seeking brotherhood, a word of prayer, or a next step in your walk,
              we'd be honored to walk with you. Send us a note.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7">
            <form onSubmit={submitContact} data-testid="contact-form" className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" required />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" required />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Inquiry Type</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="contact-type"
                  className="w-full bg-transparent border-b border-charcoal/30 py-3 font-sans focus:outline-none focus:border-olive transition-colors">
                  <option>General</option>
                  <option>Men's Ministry</option>
                  <option>Prayer Request</option>
                </select>
              </div>
              <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" required />
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Message</label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={5}
                  data-testid="contact-message"
                  className="w-full bg-transparent border-b border-charcoal/30 py-3 font-sans focus:outline-none focus:border-olive transition-colors resize-none" />
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit"
                className="group inline-flex items-center gap-3 bg-charcoal text-bone px-10 py-4 font-mono text-xs uppercase tracking-widest hover:bg-olive transition-colors duration-300 disabled:opacity-50">
                {sending ? "Sending..." : "Send Message"} <ArrowRight size={16} strokeWidth={1.5} className="hover-arrow" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-charcoal/30 py-3 font-sans focus:outline-none focus:border-olive transition-colors" />
    </div>
  );
}
